import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { environment } from 'src/environments/environment';

import {
  INITIAL_SPEED_TEST_SNAPSHOT,
  isSpeedTestRunning,
  SpeedTestSnapshot,
  SpeedTestStatus,
} from './speed-test.types';

const ENGINE_SCRIPT_SRC = '/assets/librespeed/speedtest.js';

@Injectable({
  providedIn: 'root',
})
export class SpeedTestService {
  private readonly snapshotSubject = new BehaviorSubject<SpeedTestSnapshot>({
    ...INITIAL_SPEED_TEST_SNAPSHOT,
  });
  readonly snapshot$ = this.snapshotSubject.asObservable();

  private engine: LibreSpeedEngine | null = null;
  private scriptPromise: Promise<void> | null = null;
  private userAborted = false;

  constructor(private readonly ngZone: NgZone) {}

  get snapshot(): SpeedTestSnapshot {
    return this.snapshotSubject.value;
  }

  async start(): Promise<void> {
    if (isSpeedTestRunning(this.snapshot.status)) {
      return;
    }

    this.userAborted = false;
    this.disposeEngine();
    this.snapshotSubject.next({
      ...INITIAL_SPEED_TEST_SNAPSHOT,
      status: 'preparing',
    });

    try {
      await this.ensureEngineLoaded();
      const engine = this.createConfiguredEngine();
      this.engine = engine;
      engine.start();
    } catch {
      this.snapshotSubject.next({
        ...this.snapshot,
        status: 'failed',
        errorMessage: 'No se pudo iniciar el motor de medición. Intente nuevamente.',
      });
    }
  }

  abort(): void {
    if (!isSpeedTestRunning(this.snapshot.status)) {
      return;
    }

    this.userAborted = true;
    try {
      this.engine?.abort();
    } catch {
      this.snapshotSubject.next({
        ...this.snapshot,
        status: 'aborted',
        errorMessage: 'Prueba cancelada.',
      });
    }
  }

  dispose(): void {
    this.userAborted = false;
    this.disposeEngine();
    this.snapshotSubject.next({ ...INITIAL_SPEED_TEST_SNAPSHOT });
  }

  private createConfiguredEngine(): LibreSpeedEngine {
    const engine = new window.Speedtest();
    engine.setParameter('telemetry_level', 'none');
    engine.setParameter('getIp_ispInfo', false);
    engine.setParameter('xhr_dlMultistream', 6);
    engine.setParameter('xhr_ulMultistream', 6);

    const server: LibreSpeedServer = {
      name: 'Infistel',
      server: environment.SPEEDTEST_URL,
      dlURL: 'backend/garbage.php',
      ulURL: 'backend/empty.php',
      pingURL: 'backend/empty.php',
      getIpURL: 'backend/getIP.php',
    };

    engine.addTestPoint(server);
    engine.setSelectedServer(server);
    engine.onupdate = (data) => {
      this.ngZone.run(() => this.applyUpdate(data));
    };
    engine.onend = (aborted) => {
      this.ngZone.run(() => this.applyEnd(aborted));
    };

    return engine;
  }

  private applyUpdate(data: LibreSpeedUpdate): void {
    const mappedStatus = this.mapTestState(data.testState);
    const status =
      mappedStatus === 'completed' || mappedStatus === 'aborted' || mappedStatus === 'failed'
        ? this.snapshot.status
        : mappedStatus;

    this.snapshotSubject.next({
      ...this.snapshot,
      status,
      downloadMbps: this.parseMetric(data.dlStatus),
      uploadMbps: this.parseMetric(data.ulStatus),
      pingMs: this.parseMetric(data.pingStatus),
      jitterMs: this.parseMetric(data.jitterStatus),
      downloadProgress: this.clampProgress(data.dlProgress),
      uploadProgress: this.clampProgress(data.ulProgress),
      pingProgress: this.clampProgress(data.pingProgress),
      errorMessage: null,
    });
  }

  private applyEnd(aborted: boolean): void {
    if (this.userAborted) {
      this.snapshotSubject.next({
        ...this.snapshot,
        status: 'aborted',
        errorMessage: 'Prueba cancelada.',
      });
      return;
    }

    if (aborted) {
      this.snapshotSubject.next({
        ...this.snapshot,
        status: 'failed',
        errorMessage: 'La prueba se interrumpió. Intente nuevamente.',
      });
      return;
    }

    const current = this.snapshot;
    const hasValidResults =
      current.downloadMbps !== null &&
      current.uploadMbps !== null &&
      current.pingMs !== null &&
      current.jitterMs !== null;

    if (!hasValidResults) {
      this.snapshotSubject.next({
        ...current,
        status: 'failed',
        errorMessage: 'La prueba no devolvió resultados válidos. Intente nuevamente.',
      });
      return;
    }

    this.snapshotSubject.next({
      ...current,
      status: 'completed',
      downloadProgress: 1,
      uploadProgress: 1,
      pingProgress: 1,
      errorMessage: null,
    });
  }

  private mapTestState(testState: number): SpeedTestStatus {
    switch (testState) {
      case 0:
        return 'preparing';
      case 1:
        return 'download';
      case 2:
        return 'ping';
      case 3:
        return 'upload';
      case 4:
        return 'completed';
      case 5:
        return 'aborted';
      default:
        return 'preparing';
    }
  }

  private parseMetric(value: string): number | null {
    if (value === '' || value === 'Fail') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clampProgress(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.min(1, Math.max(0, value));
  }

  private ensureEngineLoaded(): Promise<void> {
    if (typeof window.Speedtest === 'function') {
      return Promise.resolve();
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[src="${ENGINE_SCRIPT_SRC}"]`);
      if (existing) {
        if (typeof window.Speedtest === 'function') {
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('LibreSpeed script failed')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = ENGINE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => {
        if (typeof window.Speedtest === 'function') {
          resolve();
          return;
        }
        reject(new Error('LibreSpeed constructor missing'));
      };
      script.onerror = () => reject(new Error('LibreSpeed script failed'));
      document.body.appendChild(script);
    }).catch((error) => {
      this.scriptPromise = null;
      throw error;
    });

    return this.scriptPromise;
  }

  private disposeEngine(): void {
    const engine = this.engine;
    if (!engine) {
      return;
    }

    engine.onupdate = null;
    engine.onend = null;

    try {
      if (engine.getState() === 3) {
        engine.abort();
      }
    } catch {
      // el motor puede no estar en un estado abortable
    }

    if (engine.worker) {
      engine.worker.terminate();
    }

    this.engine = null;
  }
}
